import type { WorkspaceVersionGroup } from '@stratumn/studio-shared';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_OPERATOR,
  FILTER_PARAM_KEYS,
  type FilterOperator,
  type FilterType,
  type GenerationFilters,
  OPERATOR_PARAM_KEYS
} from './types';

const VALID_OPERATORS = new Set<string>([
  'is',
  'is_not',
  'is_any_of',
  'is_not_any_of'
]);

const isFilterOperator = (value: string): value is FilterOperator =>
  VALID_OPERATORS.has(value);

/**
 * Parses an operator value (typically read from URL search params) and falls
 * back to {@link DEFAULT_OPERATOR} when the value is missing or unknown.
 *
 * Exported for unit testing.
 */
export const parseOperator = (raw: string | null): FilterOperator => {
  if (raw && isFilterOperator(raw)) {
    return raw;
  }
  return DEFAULT_OPERATOR;
};

/**
 * Reads generation filter state from URL search params and provides
 * setters that update the URL (resetting page to 1 on any change).
 */
export const useGenerationFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: GenerationFilters = useMemo(
    () => ({
      workspace_id: {
        values: searchParams.getAll(FILTER_PARAM_KEYS.workspace),
        operator: parseOperator(searchParams.get(OPERATOR_PARAM_KEYS.workspace))
      },
      version_group: {
        values: searchParams.getAll(FILTER_PARAM_KEYS.version_group),
        operator: parseOperator(
          searchParams.get(OPERATOR_PARAM_KEYS.version_group)
        )
      },
      created_by: {
        values: searchParams.getAll(FILTER_PARAM_KEYS.user),
        operator: parseOperator(searchParams.get(OPERATOR_PARAM_KEYS.user))
      }
    }),
    [searchParams]
  );

  const activeFilterTypes: FilterType[] = useMemo(() => {
    const types: FilterType[] = [];
    if (filters.workspace_id.values.length > 0) {
      types.push('workspace');
    }
    if (filters.version_group.values.length > 0) {
      types.push('version_group');
    }
    if (filters.created_by.values.length > 0) {
      types.push('user');
    }
    return types;
  }, [filters]);

  const hasActiveFilters = activeFilterTypes.length > 0;

  /**
   * Builds a new URLSearchParams from the current ones,
   * replacing the values + operator for a specific filter while preserving others.
   * Always resets page to 1.
   */
  const buildParams = useCallback(
    (
      paramKey: string,
      operatorKey: string,
      values: string[],
      operator: FilterOperator
    ) => {
      const next = new URLSearchParams();

      // Preserve all params except the ones being updated and page
      for (const [key, value] of searchParams.entries()) {
        if (key !== paramKey && key !== operatorKey && key !== 'page') {
          next.append(key, value);
        }
      }

      // Set new values
      for (const value of values) {
        next.append(paramKey, value);
      }

      // Set operator (only if filter has values)
      if (values.length > 0) {
        next.set(operatorKey, operator);
      }

      next.set('page', '1');

      return next;
    },
    [searchParams]
  );

  const setWorkspaceFilter = useCallback(
    (values: string[], operator: FilterOperator) => {
      setSearchParams(
        buildParams(
          FILTER_PARAM_KEYS.workspace,
          OPERATOR_PARAM_KEYS.workspace,
          values,
          operator
        )
      );
    },
    [buildParams, setSearchParams]
  );

  const setVersionGroupFilter = useCallback(
    (values: WorkspaceVersionGroup[], operator: FilterOperator) => {
      setSearchParams(
        buildParams(
          FILTER_PARAM_KEYS.version_group,
          OPERATOR_PARAM_KEYS.version_group,
          values,
          operator
        )
      );
    },
    [buildParams, setSearchParams]
  );

  const setUserFilter = useCallback(
    (values: string[], operator: FilterOperator) => {
      setSearchParams(
        buildParams(
          FILTER_PARAM_KEYS.user,
          OPERATOR_PARAM_KEYS.user,
          values,
          operator
        )
      );
    },
    [buildParams, setSearchParams]
  );

  const removeFilter = useCallback(
    (type: FilterType) => {
      const paramKey = FILTER_PARAM_KEYS[type];
      const operatorKey = OPERATOR_PARAM_KEYS[type];
      setSearchParams(buildParams(paramKey, operatorKey, [], DEFAULT_OPERATOR));
    },
    [buildParams, setSearchParams]
  );

  const clearAllFilters = useCallback(() => {
    const next = new URLSearchParams();
    next.set('page', '1');
    setSearchParams(next);
  }, [setSearchParams]);

  return {
    filters,
    activeFilterTypes,
    hasActiveFilters,
    setWorkspaceFilter,
    setVersionGroupFilter,
    setUserFilter,
    removeFilter,
    clearAllFilters
  };
};
